import React, { useEffect, useState } from 'react'
import { DeleteTwoTone, PlusOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { CriteriaTitle, CriteriaWrapper, HorizontalFormWrapper, Line, LogicTitle, OverflowSpin } from '../segment.styled'
import { Button, Checkbox, ConfigProvider, DatePicker, Form, Input, InputNumber, Select, Spin, TreeSelect } from 'antd'
import { aggregateLogic, aggregateValue, operatorConf } from './config/fixedConf'
import { DataType } from '../../../constants/DataType'
import LocationCriteria from './LocationCriteria'
import { useLocation } from 'react-router-dom'
import { Sources } from '../../../constants/Sources'
import { fetchBackendValue } from '../../../apis/segment'

/*
  ---------- FIELDS KEY FORMAT ----------
  1. {criteriaKey}-{fieldsName}, ex: 1-category
  2. {criteriaKey}-{groupName}-{fieldsName}, ex: 1-aggregate-col
  3. {criteriaKey}-{logicKey}-{fieldsName}, ex: 1-1-indicator

  ---------- FIELDS TO RESET ----------
  1. Source: Reset all except name, logic, and status (action in CriteriaForm.js)
  2. Custom Options: Reset logic value if customDependency is true
  3. Category: Reset all logic and aggregate fields under the same criteria block
  4. Logic Indicator: Reset logic function and logic value
  5. Logic Function: Reset logic value
*/

const initSetup = { 1: [] }
function CriteriaSetting (props) {
  const { form, defConfig, defPlaceholder, selectedSource, conf, selectorOpt, editMode, readySetup } = props
  const { t: ln_t } = useTranslation()
  const location = useLocation()
  const { state } = location || {}
  const { data } = state || {}

  const [criteriaNum, setCriteriaNum] = useState(initSetup)
  const [fetchedOpt, setFetchedOpt] = useState({})
  const [treeExpandedKeys, setTreeExpandedKeys] = useState({})
  const [contentLoading, setContentLoading] = useState(false)
  const [toFetchAfterReady, setToFetchAfterReady] = useState()
  const [fetchedOptions, setFetchedOptions] = useState({})
  const [confForBackendApi, setConfForBackendApi] = useState({})
  const [dataStatus, setDataStatus] = useState({})
  const [ready, setReady] = useState(false)
  const formVal = Form.useWatch() || {}
  const aggregateMethod = aggregateLogic(ln_t)
  const logicFunctions = operatorConf(ln_t)
  const allowDelete = Object.keys(criteriaNum).length > 1
  const boolOptions = [{ value: '1', label: ln_t('util.yes') }, { value: '2', label: ln_t('util.no') }]

  const { basic, category } = conf || {}
  const { useCustomComponent, extraSelector, useAggregate, useCategory, fetchAfterSelectCategory, logicOptApi, fetchAfterSelectType } = basic || {}
  const { options: catOptions, initialValue } = category || {}
  const customComponent = { [Sources.LOCATION]: <LocationCriteria {...props} /> }

  const resetBasedOnLogic = (newUpdates, check) => {
    const updateForm = Object.keys(formVal).map(i => {
      const keys = i?.split('-') || []
      const reset = check(keys)
      return reset ? ({ key: i, value: undefined }) : ({ key: i, value: formVal[i] })
    }).reduce((obj, item) => Object.assign(obj, { [item.key]: item.value }), {})
    form.setFieldsValue({ ...updateForm, ...newUpdates })
  }
  const resetExpandKeys = (key, logicNum) => {
    const temp = { ...treeExpandedKeys }
    delete temp[`${key}-${logicNum}`]
    setTreeExpandedKeys(temp)
  }
  const addInitValueToFormFields = key => form.setFieldsValue({ [`${key}-category`]: initialValue.category, [`${key}-period`]: initialValue.period })
  const handleFetchLogicOptions = async (value, extras, alwaysRefetch) => {
    if (Object.hasOwnProperty.call(fetchedOptions, value) && !alwaysRefetch) return
    const id = value?.split('-')[0]
    const res = await logicOptApi(id, extras)
    if (res?.status === 200) setFetchedOptions(prev => ({ ...prev, [value]: res?.data }))
  }

  // ---------- RESET FIELDS ---------- //
  const handleCustomChange = (key, newUpdates, value) => {
    const check = keys => {
      const ifLogicFields = keys[0] === key && !isNaN(keys[1])
      return ifLogicFields
    }
    resetBasedOnLogic(newUpdates, check)
    if (fetchAfterSelectCategory) handleFetchLogicOptions(value)
  }

  const handleCategoryChange = (key, newUpdates, value) => {
    const check = keys => keys[0] === key && keys.length > 2
    resetBasedOnLogic(newUpdates, check)
    if (fetchAfterSelectType) handleFetchLogicOptions(formVal[`${key}-extras`], value, selectedSource === Sources.GA4)
  }

  const handleLogicColChangeReset = (key, logicNum, newUpdates) => {
    form.setFieldsValue({ ...newUpdates, [`${key}-${logicNum}-operator`]: undefined, [`${key}-${logicNum}-val`]: undefined })
    resetExpandKeys(key, logicNum)
  }

  const handleLogicFuncChange = (key, logicNum, newUpdates) => {
    form.setFieldsValue({ ...newUpdates, [`${key}-${logicNum}-val`]: undefined })
    resetExpandKeys(key, logicNum)
  }

  // ---------- ADD & DELETE CRITERIA / LOGIC ---------- //
  const handleDelCriteria = key => {
    const temp = { ...criteriaNum }
    delete temp[key]
    const check = keys => keys[0] === key
    resetBasedOnLogic({}, check)
    setCriteriaNum(temp)
  }
  const handleAddCriteria = () => {
    const nowKey = Math.max(...Object.keys(criteriaNum)) + 1
    setCriteriaNum({ ...criteriaNum, [nowKey]: [] })
    if (initialValue) addInitValueToFormFields(nowKey)
  }

  const handleDelLogic = (key, logicNum) => {
    const newLogicNum = [...criteriaNum[key]].filter(i => i !== logicNum)
    const check = keys => keys[0] === key && keys[1] === `${logicNum}`
    resetBasedOnLogic({}, check)
    setCriteriaNum({ ...criteriaNum, [key]: newLogicNum })
  }
  const handleAddLogic = key => setCriteriaNum({ ...criteriaNum, [key]: criteriaNum[key].length > 0 ? [...criteriaNum[key], Math.max(...criteriaNum[key]) + 1] : [1] })

  // ---------- HANDLE TREE SELECTOR ---------- //
  const getTreeData = async (key, colKey, value, item, addParent, formValue = formVal) => {
    const { value: parent, title, level: prevLevel } = item || {}
    const colOptions = catOptions?.find(i => i.value === formValue[`${key}-category`])?.logicOptions || []
    const { fetch, copyIfEmpty } = colOptions?.find(i => i.value === colKey) || {}
    const id = value?.split('-')?.[0]
    const lastFetch = prevLevel + 1 === fetch.length

    const formatTree = ({ value, label }, extras = '') => ({
      value: `${value}-${label}${extras}${addParent ? `@${parent}` : ''}`,
      title: label,
      level: prevLevel + 1,
      selectable: lastFetch,
      checkable: lastFetch,
      fetched: lastFetch,
      children: lastFetch ? undefined : [{ value: `${value}loading`, disabled: true, title: <><Spin size="small" style={{ marginRight: '8px' }} />{ln_t('criteria.fetching')}</> }]
    })

    const res = await fetch.at(prevLevel)(id)
    if (res?.status !== 200) return []
    return res?.data?.length > 0
      ? res?.data?.map(i => formatTree(i))
      : copyIfEmpty
        ? [{ ...formatTree({ value: id, label: title }, `-${prevLevel + 1}`) }]
        : [{ value: `${id}empty`, title: ln_t('criteria.noData'), disabled: true }]
  }

  const removeDuplicates = (arr, prop) => {
    const seen = new Set()
    return arr.filter((obj) => {
      const value = obj[prop]
      if (!seen.has(value)) { seen.add(value); return true }
      return false
    })
  }
  const fetchingValue = async (key, formValue, logicVal, fetch, useBackendApi, pageNum = 1, searchVal) => {
    const pageSize = 10
    const pattern = searchVal === 'resetSearchValue' ? undefined : (searchVal || confForBackendApi[logicVal]?.pattern)
    const params = {
      id: formValue[`${key}-extras`]?.split('-')?.[0],
      logic: logicVal?.split('-')?.[0],
      pageNum,
      pageSize,
      source: formValue.type?.toUpperCase(),
      category: formValue[`${key}-category`]?.toLowerCase(),
      pattern
    }
    const firstPartyparam = {
      id: formValue[`${key}-extras`]?.split('-')?.[0],
      logic: logicVal?.split('-')?.[0],
      pageNum,
      pageSize,
      source: 'CSV',
      pattern: searchVal ? pattern : undefined
    }
    const res = await (formVal.type === '1st-Party' ? fetchBackendValue(firstPartyparam) : fetch(useBackendApi ? params : formValue[`${key}-extras`]?.split('-')?.[1]))

    if (res?.status === 200) {
      let newData = []
      const dataSize = !Array.isArray(res.data) ? res?.data.dataSize : res?.dataSize
      setFetchedOpt(prev => {
        newData = !Array.isArray(res.data) ? res?.data?.data?.map(item => ({ value: item, label: item })) : res?.data?.map(i => ({ ...i, value: `${i.value}-${i.label}` }))
        return { ...prev, [logicVal]: removeDuplicates([...searchVal ? [] : prev[logicVal] || [], ...newData], 'value') }
      })
      if (useBackendApi) { setConfForBackendApi(prev => ({ ...prev, [logicVal]: { fetch, done: (pageNum * pageSize) >= dataSize, currentPage: pageNum, loading: false, searching: false, inputPressing: false, pattern } })) }
      if (!dataStatus?.[logicVal]?.setup) { setDataStatus(prev => ({ ...prev, [logicVal]: { setup: true, haveData: Boolean(newData?.length) } })) }
    }
  }

  const fetchLogicOptions = async (e, key, logicNum, resetField, formValue) => {
    if (resetField) handleLogicColChangeReset(key, logicNum, { [`${key}-${logicNum}-col`]: e })
    const values = formValue || formVal
    const catValue = useCategory ? values[`${key}-category`] : values[`${key}-extras`]?.split('-')?.[1]?.toLowerCase()
    let { fetch, useBackendApi } = catOptions?.find(i => i.value === catValue)?.logicOptions?.find(i => i.value === e) || {}
    if (catValue === 'csv') {
      useBackendApi = true
      const type = e?.split('-')?.[1]
      if (type === 'STRING') {
        const optionId = useCategory ? values[`${key}-category`] : values[`${key}-extras`]?.split('-')?.[0]
        const param = {
          id: optionId,
          logic: e?.split('-')?.[0],
          pageNum: 1,
          pageSize: 10,
          source: 'CSV'
        }
        fetchingValue(key, values, e, () => fetchBackendValue(param), useBackendApi)
      }
    }

    if (!fetch || Object.prototype.hasOwnProperty.call(fetchedOpt, e)) return
    if (typeof fetch === 'function') return fetchingValue(key, values, e, fetch, useBackendApi)
    const data = await getTreeData(key, e, `${values[`${key}-extras`]}`, { level: 0 }, false, values)
    setFetchedOpt(prev => ({ ...prev, [e]: data }))
  }

  const handleTreeExpand = async (expandKeys = [], key, logicNum) => { // if tree expand -> fetch options under that parent
    const colKey = formVal[`${key}-${logicNum}-col`]
    const temp = [...fetchedOpt[colKey] || data || []]
    setTreeExpandedKeys({ ...treeExpandedKeys, [`${key}-${logicNum}`]: expandKeys })
    for (const x in expandKeys) {
      const i = expandKeys[x]
      const checkParent = i.split('@')
      const parent = checkParent.length > 1 ? checkParent[checkParent.length - 1] : false
      const item = parent ? temp.find(j => j?.value === parent)?.children?.find(j => j?.value === i) : temp.find(j => j?.value === i) || {}
      if (item?.fetched) continue
      item.children = await getTreeData(key, colKey, i, item, true)
      item.fetched = true
    }
    setFetchedOpt(prev => ({ ...prev, [colKey]: temp }))
  }

  // ----- RESET BACK TO INIT SETTING WHEN SOURCE CHANGE -----
  useEffect(() => {
    if (editMode) return // since in edit source can't change, directly return
    setCriteriaNum(initSetup)
    setFetchedOpt({})
    if (initialValue) addInitValueToFormFields('1')
  }, [conf])

  // ----- IF EDIT MODE, SETUP FORM VALUE -----
  const fetchAllOptions = async (list, formValue) => {
    if (!list.length) setReady(true)
    for (const x in list) {
      const { column, key, logicNum } = list[x]
      await fetchLogicOptions(column, key, logicNum, false, formValue)
      if (Number(x) + 1 === list.length) setReady(true)
    }
  }
  useEffect(() => {
    const { UISetting } = data || {}
    if (!data || !UISetting || !UISetting.length || !readySetup) return
    setContentLoading(true)
    const num = {}; const checkToFetch = []; const fetchDetails = []; let setupForm = {}
    UISetting.forEach(async (item, idx) => {
      const key = idx + 1
      const { category, period, extras, indic, setting } = item || {}
      const { col, func, val } = indic || {}
      num[key] = []
      setupForm = {
        ...setupForm,
        ...indic ? { [`${key}-aggregate`]: true, [`${key}-aggregate-col`]: col, [`${key}-aggregate-func`]: func, [`${key}-aggregate-val`]: val } : {},
        [`${key}-category`]: category,
        [`${key}-period`]: period,
        [`${key}-extras`]: extras
      }

      if (!setting || !setting.length) return
      setting.forEach(async ({ col, operator, val, isTotal, parent }, index) => {
        const logicNum = index + 1
        const column = `${col}${isTotal ? `-${isTotal}` : ''}`
        num[key].push(logicNum)
        checkToFetch.push({ column, key, logicNum })
        const { fetchDetail } = catOptions?.find(i => i.value === category)?.logicOptions?.find(i => i.value === column) || {}
        if (fetchDetail) fetchDetails.push({ key, logicNum, value: parent, setupForm: { [`${key}-${logicNum}-val`]: val } })
        if (fetchAfterSelectCategory) handleFetchLogicOptions(extras)
        setupForm = { ...setupForm, [`${key}-${logicNum}-col`]: column, [`${key}-${logicNum}-operator`]: operator, [`${key}-${logicNum}-val`]: fetchDetail ? undefined : val }
      })
    })

    form.setFieldsValue(setupForm)
    setCriteriaNum(num)
    setToFetchAfterReady(fetchDetails)
    fetchAllOptions([...new Set(checkToFetch)], setupForm)
  }, [readySetup])

  const fetchAllDetails = async list => {
    for (const x in list) {
      const { key, logicNum, value, setupForm } = list[x]
      await handleTreeExpand(value, key, logicNum)
      form.setFieldsValue(setupForm)
      if (Number(x) + 1 === list.length) { setContentLoading(false); setToFetchAfterReady() }
    }
  }
  useEffect(() => {
    if (!ready) return
    if (!toFetchAfterReady?.length) return setContentLoading(false)
    fetchAllDetails(toFetchAfterReady)
  }, [ready])

  // ----- backend selector setting -----
  const loadOnScroll = ({ target }, key, logicVal) => {
    if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
      const { fetch, currentPage, done } = confForBackendApi[logicVal]

      if (done) return
      setConfForBackendApi(prev => ({ ...prev, [logicVal]: { ...prev[logicVal] || {}, loading: true } }))
      fetchingValue(key, formVal, logicVal, fetch, true, currentPage + 1)
    }
  }
  const handleSearchClicked = logicVal => setConfForBackendApi(prev => ({ ...prev, [logicVal]: { ...prev[logicVal] || {}, inputPressing: true } }))
  const handleSearch = async (value, key, logicVal) => {
    const { fetch } = confForBackendApi[logicVal]
    setConfForBackendApi(prev => ({ ...prev, [logicVal]: { ...prev[logicVal] || {}, searching: true } }))
    fetchingValue(key, formVal, logicVal, fetch, true, 1, value)
  }

  return (
    <>
      {useCustomComponent
        ? customComponent[selectedSource]
        : <div style={{ position: 'relative' }}>
          {contentLoading ? <OverflowSpin><Spin /></OverflowSpin> : ''}
          {Object.keys(criteriaNum).map(key => (
            <CriteriaWrapper key={key}>
              <CriteriaTitle>
                {ln_t('segment.criteria')} #{key}
                {allowDelete ? <DeleteTwoTone style={{ cursor: 'pointer' }} twoToneColor="#707070" onClick={() => handleDelCriteria(key)} /> : ''}
                <Line small={allowDelete} />
              </CriteriaTitle>

              <HorizontalFormWrapper>
                {extraSelector
                  ? <Form.Item {...defConfig(extraSelector.label, 'select')} name={`${key}-extras`}>
                      {extraSelector?.useTree
                        ? <TreeSelect placeholder={defPlaceholder(extraSelector.label, 'select')} treeData={selectorOpt} onChange={e => handleCustomChange(key, { [`${key}-extras`]: e })} />
                        : <Select
                            showSearch
                            placeholder={defPlaceholder(extraSelector.label, 'select')}
                            options={selectorOpt}
                            optionFilterProp="children"
                            onChange={e => handleCustomChange(key, { [`${key}-extras`]: e }, e)}
                          />}
                    </Form.Item>
                  : ''}
                {useCategory
                  ? <Form.Item {...defConfig('category', 'input')} name={`${key}-category`}>
                      <Select
                        showSearch
                        placeholder={defPlaceholder('category', 'select')}
                        options={catOptions}
                        optionFilterProp="children"
                        onChange={e => handleCategoryChange(key, { [`${key}-category`]: e }, e)}
                      />
                    </Form.Item>
                  : ''}
                <Form.Item {...defConfig('time', 'input')} name={`${key}-period`}>
                  <InputNumber style={{ width: '100%' }} precision={0} min={1} max={180} placeholder={defPlaceholder('time', 'input')} />
                </Form.Item>
              </HorizontalFormWrapper>

              {/* ---------- Aggregate ---------- */}
              {useAggregate
                ? <>
                  <Form.Item noStyle name={`${key}-aggregate`} valuePropName="checked">
                    <Checkbox style={formVal[`${key}-aggregate`] ? { paddingBottom: '8px' } : { paddingBottom: '16px' }}>
                      {ln_t('criteria.aggregateData')}
                    </Checkbox>
                  </Form.Item>
                  {formVal[`${key}-aggregate`]
                    ? <HorizontalFormWrapper>
                        <Form.Item name={`${key}-aggregate-col`} rules={[{ required: true, message: defPlaceholder('aggregate.indicator', 'select') }]}>
                            <Select
                              showSearch
                              placeholder={ln_t('criteria.aggregate.indicator')}
                              options={catOptions?.find(i => i.value === formVal[`${key}-category`])?.aggregateOptions}
                              optionFilterProp="children"
                              disabled={!formVal[`${key}-category`]}
                            />
                        </Form.Item>
                        <Form.Item name={`${key}-aggregate-func`} rules={[{ required: true, message: defPlaceholder('aggregate.method', 'select') }]}>
                          <Select
                            showSearch
                            placeholder={ln_t('criteria.aggregate.method')}
                            options={aggregateMethod}
                            optionFilterProp="children"
                          />
                        </Form.Item>
                        <Form.Item name={`${key}-aggregate-val`} rules={[{ required: true, message: defPlaceholder('aggregate.percentage', 'select') }]}>
                          <Select
                            showSearch
                            placeholder={ln_t('criteria.aggregate.percentage')}
                            options={aggregateValue}
                            optionFilterProp="children"
                          />
                        </Form.Item>
                      </HorizontalFormWrapper>
                    : ''}
                </>
                : ''
              }

              {/* ---------- Logic ---------- */}
              {criteriaNum[key]?.map(logicNum => {
                const catValue = useCategory ? formVal[`${key}-category`] : formVal[`${key}-extras`]?.split('-')?.[1]?.toLowerCase()
                const colOptions = catOptions?.find(i => i.value === catValue)?.logicOptions || fetchedOptions[formVal[`${key}-extras`]]?.find(i => i.value === 'default')?.logicOptions
                const { type: defType, fetch, useBackendApi } = colOptions?.find(i => i.value === formVal[`${key}-${logicNum}-col`]) || {}
                const selectedCol = formVal[`${key}-${logicNum}-col`]
                const type = selectedCol?.split('-')?.[0] === 'd' || selectedCol?.split('-')?.[0] === 'm' ? DataType.DATE : defType
                const funcOptions = logicFunctions[type] || []
                const { component: c = 'input', precision, multiple, time } = funcOptions.find(i => i.value === formVal[`${key}-${logicNum}-operator`]) || {}
                const component = c.includes('picker') ? c : typeof fetch === 'function' ? 'select' : Array.isArray(fetch) ? 'tree' : c
                const valProps = { placeholder: ln_t('criteria.logics.value'), disabled: !formVal[`${key}-${logicNum}-operator`] }
                return (
                  <div key={`${key}-${logicNum}`}>
                    <LogicTitle>
                      {ln_t('criteria.logic')} #{logicNum}
                      <DeleteTwoTone style={{ cursor: 'pointer' }} twoToneColor="#707070" onClick={() => handleDelLogic(key, logicNum)} />
                    </LogicTitle>
                    <HorizontalFormWrapper>
                      <Form.Item name={`${key}-${logicNum}-col`} rules={[{ required: true, message: defPlaceholder('logics.indicator', 'select') }]}>
                        <Select
                          showSearch
                          placeholder={ln_t('criteria.logics.indicator')}
                          options={colOptions || fetchedOptions[formVal[`${key}-extras`]]?.find(i => i.value === 'default')?.logicOptions}
                          optionFilterProp="children"
                          disabled={!formVal[`${key}-category`] && useCategory}
                          onChange={e => fetchLogicOptions(e, key, logicNum, true)}
                        />
                      </Form.Item>
                      <Form.Item name={`${key}-${logicNum}-operator`} rules={[{ required: true, message: defPlaceholder('logics.function', 'select') }]}>
                        <Select
                          showSearch
                          placeholder={ln_t('criteria.logics.function')}
                          options={funcOptions}
                          optionFilterProp="children"
                          disabled={!formVal[`${key}-${logicNum}-col`]}
                          onChange={e => handleLogicFuncChange(key, logicNum, { [`${key}-${logicNum}-operator`]: e })}
                        />
                      </Form.Item>
                      <Form.Item name={`${key}-${logicNum}-val`} rules={[{ required: true, message: defPlaceholder('logics.value', 'select') }]}>
                        {component === 'tree' && dataStatus[selectedCol]?.haveData
                          ? <TreeSelect
                              {...valProps}
                              showSearch
                              treeCheckable={multiple}
                              treeData={fetchedOpt[selectedCol]}
                              treeExpandedKeys={treeExpandedKeys[`${key}-${logicNum}`]}
                              onTreeExpand={e => handleTreeExpand(e, key, logicNum)}
                            />
                          : component === 'select' && dataStatus[selectedCol]?.haveData
                            ? <Select
                                {...valProps}
                                showSearch
                                mode={multiple ? fetch ? 'multiple' : 'tags' : ''}
                                options={type === DataType.BOOL
                                  ? boolOptions
                                  : [
                                      ...fetchedOpt[selectedCol] || [],
                                      ...confForBackendApi[selectedCol]?.loading
                                        ? [{ value: 'loading', label: <><Spin size="small" style={{ marginRight: '8px' }} />{ln_t('criteria.fetching')}</>, disabled: true }]
                                        : []
                                    ] || []}
                                {...useBackendApi
                                  ? {
                                      onPopupScroll: e => loadOnScroll(e, key, selectedCol),
                                      onInputKeyDown: ({ code, target }) => {
                                        if (code === 'Enter') {
                                          handleSearch(target.value, key, selectedCol)
                                          setDataStatus(prev => ({ ...prev, [selectedCol]: { ...prev[selectedCol] || {}, searched: true } }))
                                        } else handleSearchClicked(selectedCol)
                                      },
                                      dropdownRender: menu => confForBackendApi[selectedCol]?.searching
                                        ? <Spin size="small" style={{ marginRight: '8px' }} />
                                        : confForBackendApi[selectedCol]?.inputPressing
                                          ? <span style={{ color: 'rgba(0,0,0,.25)' }}>{ln_t('location.searchInfo')}</span>
                                          : menu,
                                      filterOption: false,
                                      onDropdownVisibleChange: e => {
                                        if (e === false && dataStatus[selectedCol]?.searched) {
                                          handleSearch('resetSearchValue', key, selectedCol)
                                          setDataStatus(prev => ({ ...prev, [selectedCol]: { ...prev[selectedCol] || {}, searched: false } }))
                                        }
                                      }
                                    }
                                  : {}
                                }
                              />
                            : component === 'datepicker'
                              ? <DatePicker {...valProps} showTime={time} showSecond={false} placeholder={ln_t('criteria.selectDate')} style={{ width: '100%' }} placement="bottomRight"/>
                              : component === 'rangepicker'
                                ? <DatePicker.RangePicker {...valProps} showTime={time} showSecond={false} placeholder={[ln_t('criteria.selectDate'), ln_t('criteria.selectDate')]} style={{ width: '100%' }} placement="bottomRight"/>
                                : component === 'inputnumber'
                                  ? <InputNumber {...valProps} precision={precision} style={{ width: '100%' }}/>
                                  : <Input {...valProps}/>
                        }
                      </Form.Item>
                    </HorizontalFormWrapper>
                  </div>
                )
              })}

              <ConfigProvider theme={{ token: { colorPrimary: 'rgba(75,75,75,1)', colorPrimaryHover: 'rgba(75,75,75,.8)' } }}>
                <Button type="primary" style={{ width: '100%', marginTop: '16px' }} icon={<PlusOutlined />} onClick={() => handleAddLogic(key)}>
                  {ln_t('util.add')} {ln_t('criteria.criteriaLogic')}
                </Button>
              </ConfigProvider>
            </CriteriaWrapper>
          ))}
          <Button type="default" style={{ width: '100%' }} icon={<PlusOutlined />} onClick={handleAddCriteria}>
            {ln_t('util.add')} {ln_t('segment.criteria')}
          </Button>
        </div>
      }
    </>
  )
}

export default CriteriaSetting
